import { Injectable, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { map, mergeMap, reduce, shareReplay, tap } from 'rxjs/operators';
import {
    chunk as _chunk,
    get as _get,
    toString as _toString,
    trim as _trim,
    uniq as _uniq,
} from 'lodash';
import { UnifiedQueryV2Service } from './unified-query-v2.service';

// SKUs per $in query when resolving a batch.
const LOOKUP_CHUNK_SIZE = 100;

// One product as the lnwshop crawler records it (lnw_crawler.inventory),
// reduced to what a product card shows. The crawler is the only place the
// console can turn a seller SKU into a product with a picture and a stock
// figure; the marketplaces themselves only echo the SKU back.
export interface InventoryProduct {
    pid: number;
    sku: string;
    title: string;
    price: number | null;
    thumbnail: string;
    remain: number | null;
    reserve: number | null;
    sold: number | null;
    stockStatus: string;
    productType: string;
    // Storefront page on genlogic.co.th.
    url: string;
    // Back-office page on lnwstore, where stock is edited.
    adminUrl: string;
    // Per-marketplace listing pages, when the crawler recorded them.
    marketplaceUrls: { label: string; url: string }[];
    crawlerLastUpdated: string;
}

const ADMIN_PRODUCT_URL = 'https://a.lnwstore.com/arduino4/inventory/product/';

export const adminProductUrl = (pid: number): string =>
    `${ADMIN_PRODUCT_URL}${pid}/`;

const MARKETPLACE_LABELS: [string, string][] = [
    ['lazada', 'Lazada'],
    ['shopee', 'Shopee'],
    ['tiktok_shop', 'TikTok Shop'],
    ['line_shopping', 'LINE Shopping'],
];

@Injectable({
    providedIn: 'root',
})
export class ProductLookupService {
    private unifiedQuery = inject(UnifiedQueryV2Service);
    // Per-SKU results for the life of the session: a card is opened for the
    // same few SKUs over and over while packages are checked in, and stock
    // a few minutes stale is fine for a card whose links lead to the live
    // pages.
    private cache = new Map<string, Observable<InventoryProduct | null>>();

    // The product carrying this seller SKU, or null when the crawler has no
    // such SKU (a marketplace-only SKU, or one renamed since the last crawl).
    findBySku(sku: string): Observable<InventoryProduct | null> {
        const key = _trim(sku);
        if (!key) {
            return of(null);
        }
        let cached = this.cache.get(key);
        if (!cached) {
            cached = this.unifiedQuery
                .query<any>({
                    db: 'lnw_crawler',
                    col: 'inventory',
                    query: { sku: key },
                    options: { limit: 1 },
                })
                .pipe(
                    map((rows) =>
                        rows.length
                            ? ProductLookupService.toProduct(rows[0])
                            : null,
                    ),
                    // A failed lookup must not be cached as "no product".
                    tap({ error: () => this.cache.delete(key) }),
                    shareReplay(1),
                );
            this.cache.set(key, cached);
        }
        return cached;
    }

    // Several SKUs in one query, resolved to a map by SKU (absent when the
    // crawler has none). Fills the per-SKU cache on the way, so cards opened
    // afterwards for these SKUs are instant.
    findBySkus(skus: string[]): Observable<Map<string, InventoryProduct>> {
        const keys = _uniq(skus.map((sku) => _trim(sku)).filter(Boolean));
        if (keys.length === 0) {
            return of(new Map());
        }
        return from(_chunk(keys, LOOKUP_CHUNK_SIZE)).pipe(
            mergeMap(
                (chunk) =>
                    this.unifiedQuery.query<any>({
                        db: 'lnw_crawler',
                        col: 'inventory',
                        query: { sku: { $in: chunk } },
                        options: { limit: chunk.length },
                    }),
                2,
            ),
            reduce((all: any[], rows: any[]) => all.concat(rows), [] as any[]),
            map((rows) => {
                const found = new Map<string, InventoryProduct>();
                for (const row of rows) {
                    const product = ProductLookupService.toProduct(row);
                    found.set(product.sku, product);
                }
                for (const key of keys) {
                    this.cache.set(key, of(found.get(key) ?? null));
                }
                return found;
            }),
        );
    }

    private static toProduct(row: any): InventoryProduct {
        const pid = Number(row.pid ?? row._id);
        const urls = row.url || {};
        const marketplaceUrls: { label: string; url: string }[] = [];
        for (const [field, label] of MARKETPLACE_LABELS) {
            // Each marketplace holds one url per shop, keyed by the crawler's
            // channel id; the shop id is not worth showing, the link is.
            const perShop = urls[field];
            if (perShop && typeof perShop === 'object') {
                Object.values(perShop).forEach((url, index, all) => {
                    if (typeof url === 'string' && url) {
                        marketplaceUrls.push({
                            label:
                                all.length > 1
                                    ? `${label} ${index + 1}`
                                    : label,
                            url,
                        });
                    }
                });
            }
        }
        return {
            pid,
            sku: _toString(row.sku ?? ''),
            title: _toString(row.title ?? ''),
            price: ProductLookupService.toNumberOrNull(
                _get(row, 'price.$numberDecimal', row.price),
            ),
            thumbnail: _toString(row.thumbnail ?? ''),
            remain: ProductLookupService.toNumberOrNull(row.remain),
            reserve: ProductLookupService.toNumberOrNull(row.reserve),
            sold: ProductLookupService.toNumberOrNull(row.sell),
            stockStatus: _toString(row.stock_status ?? ''),
            productType: _toString(row.product_type ?? ''),
            url: _toString(row.purl || urls.lnwshop || ''),
            adminUrl: Number.isFinite(pid) ? adminProductUrl(pid) : '',
            marketplaceUrls,
            crawlerLastUpdated: _toString(row.crawler_last_updated ?? ''),
        };
    }

    private static toNumberOrNull(value: unknown): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
}
